"use client";

import { FormEvent, useState } from "react";
import SiteHeader from "@/components/SiteHeader";

type FormState = {
  name: string;
  email: string;
  message: string;
};

export default function ContactPage() {
  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    message: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    setSubmitting(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      const data = (await res.json()) as {
        ok: boolean;
        message?: string;
        error?: string;
      };

      if (!res.ok || !data.ok) {
        setErrorMessage(data.error ?? "Something went wrong.");
        return;
      }

      setSuccessMessage(data.message ?? "Your request has been received.");
      setForm({
        name: "",
        email: "",
        message: "",
      });
    } catch {
      setErrorMessage("Unable to submit your request right now.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen bg-gray-100">
      <SiteHeader />

      <section className="px-4 py-20">
        <div className="mx-auto max-w-4xl">
          <div className="mb-10 rounded-xl bg-white p-8 shadow">
            <h1 className="mb-4 text-3xl font-bold">
              Contact Brown Trout Systems
            </h1>

            <p className="text-gray-600">
              Need help with computer support, remote assistance, or proactive
              system management? Reach out and we will discuss the best way to
              support your business or home office.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-xl bg-white p-8 shadow">
              <h2 className="mb-4 text-2xl font-semibold">
                Get in Touch
              </h2>

              <div className="space-y-4 text-gray-700">
                <p>
                  <span className="font-semibold">Email:</span>{" "}
                  <a
                    href="mailto:support@browntroutsystems.com"
                    className="text-blue-600"
                  >
                    support@browntroutsystems.com
                  </a>
                </p>

                <p>
                  <span className="font-semibold">Remote Support:</span>{" "}
                  Available by appointment and as needed for active clients.
                </p>

                <p>
                  <span className="font-semibold">Services:</span>{" "}
                  Computer consulting, remote support, managed systems, and
                  small business IT help.
                </p>
              </div>
            </div>

            <div className="rounded-xl bg-white p-8 shadow">
              <h2 className="mb-4 text-2xl font-semibold">
                Request Assistance
              </h2>

              <form
                className="space-y-4"
                onSubmit={handleSubmit}
              >
                <div>
                  <label
                    htmlFor="name"
                    className="mb-2 block text-sm font-medium"
                  >
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={(e) =>
                      setForm({ ...form, name: e.target.value })
                    }
                    className="w-full rounded-lg border px-4 py-3"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="mb-2 block text-sm font-medium"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    className="w-full rounded-lg border px-4 py-3"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="mb-2 block text-sm font-medium"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={(e) =>
                      setForm({ ...form, message: e.target.value })
                    }
                    className="w-full rounded-lg border px-4 py-3"
                    placeholder="Tell us what kind of help you need"
                  />
                </div>

                {successMessage ? (
                  <p className="text-sm text-green-700">
                    {successMessage}
                  </p>
                ) : null}

                {errorMessage ? (
                  <p className="text-sm text-red-700">
                    {errorMessage}
                  </p>
                ) : null}

                <button
                  type="submit"
                  disabled={submitting}
                  className="rounded-lg bg-black px-6 py-3 text-white disabled:opacity-60"
                >
                  {submitting ? "Sending..." : "Send Request"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
